import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
  Grid,
  Paper,
  Select,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { allBranches, allProductsViews } from "../../../data/Axios/queries";
import { companyManagementUrl, inventoryManagementUrl, setupManagementUrl } from "../../../config";
import {
  graphqlQuery,
  generateAndExecuteMutation,
} from "../../../data/Axios/DynamicService";

// Validation schema
const validationSchema = Yup.object().shape({
  sourceBranchId: Yup.number().required("Source Branch ID is required"),
  productId: Yup.number().required("Product ID is required"),
  destinationBranchId: Yup.number().required("Destination Branch is required"),
  quantity: Yup.number()
    .positive("Quantity must be positive")
    .required("Quantity is required"),
});

const InventoryBranchTransfer = () => {
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const branchResponse = await graphqlQuery(
          allBranches,
          companyManagementUrl.uri
        );
        if (branchResponse) {
          setBranches(branchResponse || []);
        }

        // const productDataResponse = await graphqlQuery(
        //   allProductsViews,
        //   "https://posdxl.mcb.co.ke/posgateway/POSSetupManagement"
        // );

        // if (Array.isArray(productDataResponse)) {
        //   const filteredProducts = productDataResponse.filter(
        //     (product) => product.isInventory === true
        //   );
        //   setProducts(filteredProducts);
        // }
      } catch (error) {
        console.error("Error fetching branches:", error);
      }
    };

    fetchBranches();
  }, []);
  const fetchtProducts = async (sourceBranchId) => {
    try {
      const query = `
          query { 
            productById(product: { branchId: ${sourceBranchId}, isInventory: true }) {
        id
        productName
        isActive
        uomId
        reorderPoint
        isInventory
        companyName
        companyId
        regionId
        branchId
        
    }
        `;

      // Execute the GraphQL query
      const response = await graphqlQuery(
        query,
        setupManagementUrl.uri
      );
      if (Array.isArray(response)) {
        const filteredProducts = response.filter(
          (product) => product.isInventory === true
        );
        setProducts(filteredProducts);
      }
      return response; // Return the mapped branch options
    } catch (error) {
      console.error("Error fetching branches:", error);
      return [];
    }
  };

  const initialValues = {
    sourceBranchId: "",
    productId: "",
    destinationBranchId: "",
    quantity: "",
    inventoryQuantity: "", // Current Quantity
    remainingQuantity: "", // Remaining Quantity
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const { sourceBranchId, productId, destinationBranchId, quantity } =
        values;

      const mutationData = {
        sourceBranchId,
        productId,
        destinationBranchId,
        quantity,
      };

      const response = await generateAndExecuteMutation(
        "inventoryBranchTransfer",
        mutationData,
        "inventory",
        inventoryManagementUrl.uri
      );

      if (response) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Inventory branch transfer was successful.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }

    setSubmitting(false);
  };

  const handleProductChange = (e, setFieldValue) => {
    const selectedProductId = e.target.value;

    const selectedProduct = products.find(
      (product) => product.id === selectedProductId
    );

    setFieldValue("productId", selectedProductId);
    setFieldValue(
      "inventoryQuantity",
      selectedProduct?.inventoryQuantity || ""
    );
    setFieldValue(
      "remainingQuantity",
      selectedProduct?.inventoryQuantity || ""
    );
  };

  const handleQuantityChange = (e, values, setFieldValue) => {
    const transferQuantity = e.target.value;
    const remainingQuantity =
      (values.inventoryQuantity || 0) - (transferQuantity || 0);

    setFieldValue("quantity", transferQuantity);
    setFieldValue(
      "remainingQuantity",
      remainingQuantity >= 0 ? remainingQuantity : 0
    );
  };

  return (
    <Paper
      elevation={3}
      sx={{ padding: 3, maxWidth: 600, margin: "auto", mt: 5 }}
    >
      <Typography variant="h5" gutterBottom>
        Inventory Branch Transfer
      </Typography>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          isSubmitting,
          handleBlur,
          setFieldValue,
          values,
          errors,
          touched,
        }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Select
                  value={values.sourceBranchId}
                  name="sourceBranchId"
                  fullWidth
                  displayEmpty
                  label="Source Branch *"
                  onBlur={handleBlur}
                  variant="outlined"
                  onChange={(e) =>
                    setFieldValue("sourceBranchId", e.target.value)
                  }
                  error={touched.sourceBranchId && !!errors.sourceBranchId}
                  helperText={touched.sourceBranchId && errors.sourceBranchId}
                >
                  <MenuItem value="" disabled>
                    Select Source Branch
                  </MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.branchName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Select
                  value={values.destinationBranchId}
                  name="destinationBranchId"
                  fullWidth
                  label="Destination Branch *"
                  variant="outlined"
                  onBlur={handleBlur}
                  displayEmpty
                  onChange={(e) =>
                    setFieldValue("destinationBranchId", e.target.value)
                  }
                  error={
                    touched.destinationBranchId && !!errors.destinationBranchId
                  }
                  helperText={
                    touched.destinationBranchId && errors.destinationBranchId
                  }
                >
                  <MenuItem value="" disabled>
                    Select Destination Branch
                  </MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.branchName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Select
                  value={values.productId}
                  name="productId"
                  fullWidth
                  displayEmpty
                  label="Product *"
                  variant="outlined"
                  onBlur={handleBlur}
                  onChange={(e) => handleProductChange(e, setFieldValue)}
                  error={touched.productId && !!errors.productId}
                  helperText={touched.productId && errors.productId}
                >
                  <MenuItem value="" disabled>
                    Select Product
                  </MenuItem>
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.productName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={3}>
                <TextField
                  variant="filled"
                  type="number"
                  label="Current Quantity *"
                  value={values.inventoryQuantity}
                  name="inventoryQuantity"
                  disabled
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  variant="filled"
                  type="number"
                  label="Remaining Quantity *"
                  onBlur={handleBlur}
                  value={values.remainingQuantity}
                  name="remainingQuantity"
                  disabled
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Quantity to Transfer *"
                  onBlur={handleBlur}
                  onChange={(e) =>
                    handleQuantityChange(e, values, setFieldValue)
                  }
                  value={values.quantity}
                  name="quantity"
                  error={touched.quantity && !!errors.quantity}
                  helperText={touched.quantity && errors.quantity}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, textAlign: "right" }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Transfer Inventory"}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Paper>
  );
};

export default InventoryBranchTransfer;
